/**
 * Utilitaire pour calculer les échéances automatiques en fonction du statut et du type de formalité
 */

// Types de formalités qui nécessitent un enregistrement aux impôts (30 jours)
const TYPES_AVEC_ENREGISTREMENT_IMPOTS = [
  "Transformation",
  "Augmentation de capital",
  "Dissolution et clôture de liquidation",
  "TUP",
  "Fusion",
];

// Types de formalités avec échéance de 3 jours à l'état TRANSMIS
const TYPES_3_JOURS_TRANSMIS = [
  "Immatriculation",
  "Dépôt d'actes",
  "BODACC",
  "Correction RNE",
];

// Types de formalités avec échéance de 7 jours à l'état TRANSMIS
const TYPES_7_JOURS_TRANSMIS = [
  "Transfert de siège",
  "Changement de dénomination sociale / nom commercial / enseigne",
  "Transformation",
  "Augmentation de capital",
  "Réduction de capital",
  "Modification de l'objet social / activités",
  "Prolongation de la durée",
  "Changement date de clôture",
  "Modification des organes de direction / associés",
  "Ouverture / fermeture / transfert d'un établissement",
  "Mise en sommeil",
  "Dissolution et clôture de liquidation",
  "TUP",
  "Fusion",
  "Déclaration des bénéficiaires effectifs",
  "Prise d'activité",
  "Mise à jour des associés",
  "Radiation El",
];

// Types de formalités avec échéance de 15 jours à l'état TRANSMIS
const TYPES_15_JOURS_TRANSMIS = [
  "Dépôt des comptes",
];

// Types de formalités avec échéance de 30 jours à l'état TRANSMIS (enregistrement impôts)
const TYPES_30_JOURS_TRANSMIS = [
  "Enregistrement impôts",
];

/**
 * Ajoute des jours à une date
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Formate une date au format YYYY-MM-DD pour Baserow
 */
function formatDateForBaserow(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calcule l'échéance automatique principale en fonction du statut et du type de formalité
 */
export function calculateDeadline(
  status: string,
  formalityTypes: string[],
  currentDate: Date = new Date()
): string | null {
  // Statut TRANSMIS
  if (status === "Transmis") {
    // Vérifier dans l'ordre du délai le plus court au plus long
    // 3 jours
    for (const type of formalityTypes) {
      if (TYPES_3_JOURS_TRANSMIS.includes(type)) {
        return formatDateForBaserow(addDays(currentDate, 3));
      }
    }
    
    // 7 jours (avant les 15 jours pour prendre le délai le plus court)
    for (const type of formalityTypes) {
      if (TYPES_7_JOURS_TRANSMIS.includes(type)) {
        return formatDateForBaserow(addDays(currentDate, 7));
      }
    }
    
    // 15 jours (après les 7 jours)
    for (const type of formalityTypes) {
      if (TYPES_15_JOURS_TRANSMIS.includes(type)) {
        return formatDateForBaserow(addDays(currentDate, 15));
      }
    }
    
    // 30 jours (après les autres délais)
    for (const type of formalityTypes) {
      if (TYPES_30_JOURS_TRANSMIS.includes(type)) {
        return formatDateForBaserow(addDays(currentDate, 30));
      }
    }
    
    // Si aucun type ne correspond, retourner null
    return null;
  }
  
  // Statut INCOMPLET ou EN COURS : 7 jours peu importe le type
  if (status === "Incomplet" || status === "En cours") {
    return formatDateForBaserow(addDays(currentDate, 7));
  }
  
  // Pour les autres statuts, pas d'échéance automatique
  return null;
}

/**
 * Calcule l'échéance impôts automatique
 * Uniquement pour statut "Transmis" aux impôts (field_7272 = "Envoyé")
 * et pour les types de formalités nécessitant un enregistrement
 */
export function calculateTaxDeadline(
  taxStatus: string | null,
  formalityTypes: string[],
  currentDate: Date = new Date()
): string | null {
  // Vérifier si le statut impôts est "Envoyé" (ce qui signifie transmis aux impôts)
  if (taxStatus !== "Envoyé") {
    return null;
  }
  
  // Vérifier si au moins un type de formalité nécessite un enregistrement aux impôts
  const needsTaxRegistration = formalityTypes.some(type => 
    TYPES_AVEC_ENREGISTREMENT_IMPOTS.includes(type)
  );
  
  if (needsTaxRegistration) {
    // Échéance de 30 jours pour l'enregistrement aux impôts
    return formatDateForBaserow(addDays(currentDate, 30));
  }
  
  return null;
}

/**
 * Vérifie si un type de formalité nécessite un enregistrement aux impôts
 */
export function requiresTaxRegistration(formalityType: string): boolean {
  return TYPES_AVEC_ENREGISTREMENT_IMPOTS.includes(formalityType);
}

/**
 * Obtient le nombre de jours d'échéance pour un type de formalité et un statut donnés
 * (utilisé pour l'affichage ou les informations)
 */
export function getDeadlineDays(status: string, formalityType: string): number | null {
  if (status === "Transmis") {
    if (TYPES_3_JOURS_TRANSMIS.includes(formalityType)) return 3;
    if (TYPES_15_JOURS_TRANSMIS.includes(formalityType)) return 15;
    if (TYPES_30_JOURS_TRANSMIS.includes(formalityType)) return 30;
    if (TYPES_7_JOURS_TRANSMIS.includes(formalityType)) return 7;
  }
  
  if (status === "Incomplet" || status === "En cours") {
    return 7;
  }
  
  return null;
}
